import {
  ApolloClient,
  ApolloError,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  from,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { RetryLink } from '@apollo/client/link/retry';
import { Store } from 'redux';
import { StoreType } from '@tock/shared/utils/connect';
import { computeFakeDataTypePolicies } from 'graphql-fake-data/fakeTicketTypes';
import { createClient } from 'graphql-ws';
import { getFilteredBusinessHeaders } from 'selectors/appSelectors';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { gqlHandlePasscodePromptError } from 'utils/passcodeUtils';
import { onError } from '@apollo/client/link/error';
import { setApolloClientConnectionState } from 'actions/AppActions';
import AppSettings from 'settings/AppSettings';
import InMemoryCacheConfig from 'generated/graphql';
import fetch from 'cross-fetch';

// Ping the server every 20 seconds to make sure the connection is still alive
// Set to same ping interval as WebsocketBase
const KEEP_ALIVE_TIME_MS = 20_000;

// After sending a ping, wait up to 60 seconds to receive a pong from the server
// before assuming the connection is dead and starting a new one
// Set to same Pong timeout as WebsocketBase
const WAIT_FOR_PONG_MS = 60_000;

// After all subscriptions are complete, wait 10 seconds before closing the
// connection. This allows a user quickly hopping between realtime and
// non-realtime pages to re-use a connection rather than creating a new one.
const CONNECTION_CLOSE_DEBOUNCE_MS = 10_000;

// Maximum number of times to try a single operation before giving up.
const RETRY_LINK_MAX_NUMBER_OF_ATTEMPTS = 100;

/**
 * By default, the graphql-ws library sends a GraphQL-specific protocol into the
 * WebSocket conection, which gets sent as a header. Undertow doesn't know what
 * to do with that header and ignores the request entirely. This little hack
 * gets around that issue by not sending up any protocols.
 */
class ProtocolFreeWebSocket extends WebSocket {
  constructor(url: string, protocols: unknown) {
    super(url, []);
  }
}

export function createLinkChain({
  jwtToken,
  activeBusinessId,
  activeBusinessGroupId,
  store,
  clientSelfReference,
  includeRetryLink,
}: {
  jwtToken: string;
  activeBusinessId: number;
  activeBusinessGroupId: number;
  store: Store;
  /**
   * The passcode error handler needs to call a mutation using the related apolloClient itself.
   * This is messy but works out okay. (The messiness is long-standing, but was hidden by
   * the previous factoring which captured an enclosing apolloClient local variable in
   * a below-the-radar sort of way.)
   */
  clientSelfReference: ApolloClient<object>;
  includeRetryLink: boolean;
}) {
  const apolloAuth = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      // NOTE! Do not casually refactor this part of the code. In some limited circumstances,
      // it seems that the setContext operation here needs to be working with a captured `store`
      // variable, and compute the filtered business headers based on asking it for current state.
      // A version of businessHeaders frozen at the time that createLinkChain was last called
      // will not be sufficient!
      headers: {
        ...headers,
        ...getFilteredBusinessHeaders(store.getState().app),
      },
    }));

    return forward(operation);
  });

  const passcodeLink = new ApolloLink((operation, forward) => {
    const passcode = operation.getContext().passcode;
    if (passcode) {
      operation.extensions = { ...operation.extensions, passcode };
    }

    return forward(operation);
  });

  const apolloHttpLink = new HttpLink({
    uri: `${AppSettings.API_BASE}/graphql`,
    credentials: 'include',
    includeExtensions: true,
    fetch: (uri, options) => {
      let opName = 'UnknownOperation';
      if (typeof options?.body === 'string') {
        const { operationName } = JSON.parse(options?.body);
        if (operationName) {
          opName = operationName;
        }
      }
      return fetch(`${uri}/${opName}`, options);
    },
  });

  let activeWebSocket: WebSocket;
  let timeout: ReturnType<typeof setTimeout>;

  const apolloWebsocketLink = new GraphQLWsLink(
    createClient({
      url: `${AppSettings.WSS_BASE}/graphql/${activeBusinessGroupId}/${activeBusinessId}?auth=${jwtToken}`,
      webSocketImpl: ProtocolFreeWebSocket,
      keepAlive: KEEP_ALIVE_TIME_MS,
      lazyCloseTimeout: CONNECTION_CLOSE_DEBOUNCE_MS,
      retryAttempts: 10, // Uses randomized exponential backoff. A value of 10 gives up after ~17 minutes.
      on: {
        connected: (socket: unknown) => {
          if (socket instanceof WebSocket) {
            activeWebSocket = socket;
          }
          store.dispatch(setApolloClientConnectionState(true));
        },
        ping: (received: boolean) => {
          const sent = !received;
          if (sent) {
            timeout = setTimeout(() => {
              if (activeWebSocket?.readyState === WebSocket.OPEN) {
                activeWebSocket.close(4408, 'Request Timeout');
              }
            }, WAIT_FOR_PONG_MS);
          }
        },
        pong: (received: boolean) => {
          if (received && timeout) {
            clearTimeout(timeout);
          }
        },
        closed: () => {
          store.dispatch(setApolloClientConnectionState(false));
        },
      },
      shouldRetry: (errOrCloseEvent) => {
        // Retry if there's an Event, which usually indicates something connection-related.
        // Consider an issue fatal (and don't retry) if the websocket server explicitly sends an error.
        return errOrCloseEvent instanceof Event;
      },
    })
  );

  // Use either the standard HTTP link or the WebSocket link based on the type
  // of query that's getting sent.
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    apolloWebsocketLink,
    apolloHttpLink
  );

  const passcodeErrorHandler = onError((error) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return new Observable((observer: any) => {
      /* eslint-enable @typescript-eslint/no-explicit-any */
      gqlHandlePasscodePromptError(error)
        .then((passcode) => {
          if (typeof passcode === 'string') {
            error.operation.setContext({ passcode });
            return clientSelfReference.mutate({
              mutation: error.operation.query,
              variables: error.operation.variables,
              context: error.operation.getContext(),
            });
          } else {
            if (!passcode) {
              throw new ApolloError({ ...error });
            }

            observer.complete();
            return Promise.resolve(null);
          }
        })
        .then((result) => {
          if (result) {
            observer.next(result);
            observer.complete();
          }
        })
        .catch((error) => {
          observer.error(error);
          observer.complete();
        });
    });
  });

  // Uses randomized exponential backoff by default
  const retryLink = new RetryLink({
    attempts: {
      max: RETRY_LINK_MAX_NUMBER_OF_ATTEMPTS,
      retryIf: (_error, operation) => {
        // Currently the GraphQL RetryLink operates / retries per-operation - this means that
        // if a client has 3 open subscriptions when a network error occurs, it will attempt to
        // retry these 3 operations (opening a new socket for each one) simultaneously.
        // As a quick win to prevent this undesirable behavior, we can refine the RetryLink so
        // that we are specifically only retrying the Timeline subscription. This was the intent when
        // initially adding the RetryLink. Longer-term, we may want to write our own custom retry strategy
        // that maintains state (delay / jitter / etc.) at an overall (per-client) level rather than per-operation.
        return operation.operationName === 'TimelineWideContextUpdates';
      },
    },
  });

  return from([
    ...(includeRetryLink ? [retryLink] : []),
    passcodeErrorHandler,
    apolloAuth,
    passcodeLink,
    splitLink,
  ]);
}

export function initializeApolloClient(store: Store<StoreType>) {
  const { app, features } = store.getState();

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache({
      ...InMemoryCacheConfig,
      typePolicies: {
        OrderEntityConnection: relayStylePagination(['sortProperty', 'search', 'sortDirection']),
        ...computeFakeDataTypePolicies(store.getState().features.features),
        TableStatesForTable: {
          fields: {
            tableStateRanges: {
              merge(existing = [], incoming) {
                // Replace the entire array with the new incoming array
                return incoming;
              },
            },
          },
        },
      },
    }),
  });
  apolloClient.setLink(
    createLinkChain({
      activeBusinessId: app.activeBusiness?.id ?? -1,
      activeBusinessGroupId: app.activeBusinessGroup?.id ?? -1,
      jwtToken: app.jwtToken,
      store,
      clientSelfReference: apolloClient,
      includeRetryLink: features.features.ENABLE_RETRY_LINK_GRAPHQL_SUBSCRIPTIONS ?? false,
    })
  );

  return apolloClient;
}
